import { theme } from '@theme';
import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import { useCheckDevice } from '../../../hooks/UseCheckDevice';
import useOnScreen from '../../../hooks/useOnScreen';
import { usePortfolioFilter } from '../../../hooks/UsePortfolioFilter';
import { AppState } from '../../../store/AppState';
import {
  setIsPortfolioFilterReset,
  setIsPortfolioFiltersChanged,
  setPortfolioRequestResource,
  updateSelectedFilters,
} from '../../../store/portfolio/PortfolioActions';
import { PortfolioState } from '../../../store/portfolio/PortfolioReducer';
import FiltersHeader from '../../shared/filters/filters-header/FiltersHeader';
import Tooltip from '../../shared/tooltip/Tooltip';
import { Icon, Typography } from '../../style';
import PortfolioFilter from './PortfolioFilter';

type PortfolioFilterSectionProps = {
  toggleBulkActions: (isBulkActionsVisible: boolean) => void;
  isPortfolioFilterSectionVisible: boolean;
};

const PortfolioFilterSection: FC<PortfolioFilterSectionProps> = ({
  toggleBulkActions,
  isPortfolioFilterSectionVisible,
}) => {
  const { hasFilters, resetFilters } = usePortfolioFilter();
  const { dealType } = useSelector<AppState, PortfolioState>(
    (portfolioState) => portfolioState.portfolio,
  );
  const [isShow, setIsShow] = useState<boolean>(false);
  const ref = useRef();
  const isVisible = useOnScreen(ref);
  const [style, setStyle] = useState('portfolio-right-container');
  const dispatch = useDispatch();
  const { isMobileDevice } = useCheckDevice();
  const toggleFilter = () => {
    if (!isShow) {
      setStyle('portfolio-right-container-slide');
      toggleBulkActions(false);
    }
    setIsShow((isShow) => (isShow ? false : true));
  };

  useEffect(() => {
    if (isShow && !isPortfolioFilterSectionVisible) {
      toggleFilter();
    }
  }, [isShow, isPortfolioFilterSectionVisible]);

  const callBack = useCallback((e: any) => {
    const dateFromNotification = e.detail.date;
    const incentiveExpiryDate = new Date(dateFromNotification);
    const count = e.detail.count;
    const year = incentiveExpiryDate.getFullYear();
    const month = (1 + incentiveExpiryDate.getMonth()).toString().padStart(2, '0');
    const day = incentiveExpiryDate.getDate().toString().padStart(2, '0');
    const formattedDate = month + '/' + day + '/' + year;
    const postData = {
      currentPage: 0,
      recordsPerPage: 10,
      search: '',
      sort: 'bps',
      sortDirection: 'desc',
      dealType: null,
      status: null,
      subStatus: [],
      bps: { min: 0, max: 100 },
      inMarketScore: { min: 0, max: 100 },
      vehicleScore: { min: 0, max: 100 },
      dealScore: { min: 0, max: 100 },
      colors: null,
      optOutPreference: null,
      currentModels: null,
      currentTrims: null,
      currentYears: null,
      currentVehicleMakes: null,
      currentVehicleType: null,
      currentVehicleRate: { min: null, max: null },
      assigneeNames: null,
      replacementModels: null,
      replacementTrims: null,
      replacementVehicleMakes: null,
      replacementYears: null,
      filteredDealTypes: [],
      replacementVehicleTypes: [],
      replacementVehicleRate: { min: null, max: null },
      lastUpdated: { min: 0, max: 9999 },
      paymentsRemaining: { min: null, max: null },
      currentVehicleMileage: { min: null, max: null },
      replacementVehicleMileage: { min: null, max: null },
      monthsOfOwnership: { min: null, max: null },
      ageDays: { min: null, max: null },
      lastServiceDate: { min: null, max: null },
      campaignActivityViewCounts: [],
      isTrimConfirmationRequired: null,
      soldBy: [],
      incentives: [],
      insights: [],
      recall: [],
      campaignExpiryDates: [formattedDate],
    };
    const data = {
      count: count,
      key: formattedDate,
      label: formattedDate,
    };
    const newSelectedFilters = {
      ageDays: null,
      assignees: [],
      bpsValue: { min: 0, max: 100 },
      inMarketScore: { min: 0, max: 100 },
      vehicleScore: { min: 0, max: 100 },
      dealScore: { min: 0, max: 100 },
      campaignExpiryDates: [data],
      colors: [],
      optOutPreference: [],
      currentVehicleMakes: [],
      currentVehicleMileage: null,
      currentVehicleModels: [],
      currentVehicleTrims: [],
      currentVehicleTypes: null,
      currentVehicleYears: [],
      currentVehicleRate: null,
      dealTypes: [],
      incentives: [],
      campaignActivityViewCounts: [],
      insights: [],
      isTrimConfirmationRequired: null,
      lastServiceDate: null,
      lastUpdated: { min: 0, max: 9999 },
      monthsOfOwnership: null,
      paymentsRemaining: null,
      recall: [],
      replacementVehicleMakes: [],
      replacementVehicleMileage: null,
      replacementVehicleModels: [],
      replacementVehicleTrims: [],
      replacementVehicleSelection: null,
      replacementVehicleTypes: [],
      replacementVehicleYears: [],
      replacementVehicleRate: null,
      soldBy: [],
      status: { key: null, label: 'All Deals', count: 37, subStatuses: [] },
      fordIncentiveNames: [],
      shopperActivity: null,
    };
    dispatch(setPortfolioRequestResource(postData, 'all'));
    dispatch(setIsPortfolioFiltersChanged(true, dealType));
    dispatch(updateSelectedFilters(newSelectedFilters, dealType));
  }, []);

  useEffect(() => {
    window.addEventListener('privateIncentiveNotificationClicked', callBack);
  }, [callBack]);

  const reset = (): void => {
    resetFilters(dealType);
    dispatch(setIsPortfolioFilterReset(true));
  };

  const filterHeaderReset = () => {
    return (
      <Typography
        className={!hasFilters(dealType) ? 'link-disabled' : ''}
        size={theme.typography.h4}
        color={hasFilters(dealType) ? theme.palette.deepBlueTwo : theme.palette.dustyTwo}
        fontWeight={theme.typography.fontWeights.semibold}
        onClick={reset}
        data-testid='filters-header-reset'
      >
        Reset
      </Typography>
    );
  };

  return (
    <div className={`${style} ${isShow ? 'filter-unpinned' : 'filter-pinned'}`}>
      {!isMobileDevice && isShow && (
        <Wrapper
          data-testid='filters'
          className={`showFilter ${isShow ? 'open-filter' : 'close-filter'}`}
        >
          <FilterHeaderWrapper>
            <FiltersHeader
              contentSpacingType='flex-start'
              contentSpacing={20}
              children={filterHeaderReset()}
            />
            <Icon
              type={'hide-right-sidebar'}
              additionalClassName='filter-icon'
              size={25}
              onClick={toggleFilter}
            />
          </FilterHeaderWrapper>
          <PortfolioFilter />
        </Wrapper>
      )}
      {!isMobileDevice && (
        <div className={`sideBar ${isShow ? 'close-sidebar' : ''}`} onClick={toggleFilter}>
          <span ref={ref}></span>
          {isVisible ? (
            <Tooltip
              trigger={
                <Icon
                  type={'show-right-sidebar'}
                  additionalClassName='filter-icon'
                  size={25}
                  marginLeft={7}
                />
              }
              content={<span>Expand filters</span>}
              cssClass='portfolio-tooltip'
            ></Tooltip>
          ) : (
            <Icon
              type={'show-right-sidebar'}
              additionalClassName='filter-icon'
              size={25}
              marginLeft={7}
              data-testid='expand-filter-icon'
            />
          )}
          <span className='sideBar-text'>Filters</span>
        </div>
      )}
    </div>
  );
};

export default PortfolioFilterSection;

const Wrapper = styled.section`
  display: flex;
  flex-direction: column;
  min-width: 240px;
  max-width: 100%;
  padding-right: 10px;
  padding-left: 10px;
  & > span {
    margin-bottom: 11px;
  }
  margin-top: 15px;
`;

const FilterHeaderWrapper = styled.section`
  display: flex;
  flex-direction: space between;
  width: 100%;
  .accordion-icon {
    margin-bottom: 21px;
    padding-top: 10px;
  }
`;
